import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useDialog } from '../../context/DialogContext'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from '../ui/Dialog'
import { Button } from '../ui/button'
import {
  Envelope,
  CircleNotch,
  Crown,
  ShieldCheck,
  User as UserIcon,
  PaperPlaneTilt,
  Question,
} from '@phosphor-icons/react'
import { sendInvite, type UserRole } from '../../services/company-users'

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  currentUserRole: UserRole
}

export function InviteUserModal({
  isOpen,
  onClose,
  onSuccess,
  currentUserRole,
}: InviteUserModalProps) {
  const { user } = useAuth()
  const { toastSuccess, toastError } = useDialog()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<UserRole>('user')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showHelp, setShowHelp] = useState(false)

  const isMaster = currentUserRole === 'master'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('E-mail é obrigatório')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('E-mail inválido')
      return
    }

    if (!user?.current_company?.id || !user?.current_company?.tenant_id) {
      setError('Erro ao identificar a empresa atual')
      return
    }

    setLoading(true)

    try {
      await sendInvite({
        email: email.trim().toLowerCase(),
        tenantId: user.current_company.tenant_id!,
        companyId: user.current_company.id,
        companyType: user.current_company.type as 'shipper' | 'freight-forwarder',
        role,
      })
      toastSuccess('Convite Enviado', `Um convite foi enviado para ${email}`)
      setEmail('')
      setRole('user')
      onSuccess()
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erro ao enviar convite'
      setError(errorMessage)
      toastError('Erro', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setEmail('')
    setRole('user')
    setError('')
    onClose()
  }

  const roleOptions: { value: UserRole; label: string; description: string; icon: React.ReactNode; disabled: boolean }[] = [
    {
      value: 'user',
      label: 'Usuário',
      description: 'Acesso operacional - pode criar cotações e visualizar dados',
      icon: <UserIcon size={20} className="text-gray-400" />,
      disabled: false,
    },
    {
      value: 'admin',
      label: 'Administrador',
      description: 'Acesso de gestão - pode gerenciar usuários e configurações',
      icon: <ShieldCheck size={20} className="text-blue-500" />,
      disabled: false,
    },
    {
      value: 'master',
      label: 'Master',
      description: 'Acesso total - controle completo da empresa',
      icon: <Crown size={20} className="text-yellow-500" />,
      disabled: !isMaster,
    },
  ]

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Convidar Usuário</h2>
              <p className="text-sm text-gray-500 mt-1">
                Envie um convite por e-mail para adicionar um novo membro à sua equipe
              </p>
            </div>
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setShowHelp((v) => !v)}
                className={`p-1.5 rounded-lg transition-colors ${
                  showHelp ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100'
                }`}
                title="Como funciona?"
                aria-label="Como funciona?"
              >
                <Question size={20} weight="bold" />
              </button>
              {showHelp && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowHelp(false)} />
                  <div className="absolute right-0 top-full mt-2 z-20 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Como funciona?</h4>
                    <ul className="text-sm text-gray-600 space-y-1.5">
                      <li>1. O usuário receberá um e-mail com link de convite</li>
                      <li>2. Se já tiver conta, será adicionado automaticamente à empresa</li>
                      <li>3. Se não tiver conta, poderá criar uma nova</li>
                      <li>4. O convite expira em 7 dias</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        <DialogContent className="space-y-6">
          {/* Email input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-mail do convidado
            </label>
            <div className="relative">
              <Envelope
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  error ? 'border-red-300' : 'border-gray-200'
                }`}
                disabled={loading}
              />
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>

          {/* Role selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nível de acesso
            </label>
            <div className="space-y-2">
              {roleOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    role === option.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={role === option.value}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    disabled={option.disabled || loading}
                    className="mt-1 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {option.icon}
                      <span className="font-medium text-gray-900">{option.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                    {option.disabled && (
                      <p className="text-xs text-amber-600 mt-1">
                        Apenas o Master pode convidar outros Masters
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

        </DialogContent>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="gap-2"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <PaperPlaneTilt size={20} />
                Enviar Convite
              </>
            )}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
